import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'Components/Atoms/Box';
import Button from 'Components/Atoms/Button';
import TextField from 'Components/Atoms/TextField';
import Typography from 'Components/Atoms/Typography';
import Modal from 'Components/Molecules/Modal';
import { AuthContext } from 'Contexts/AuthContext';
import { RestaurantContext, Severity } from 'Contexts/RestaurantContext';
import useDocument from 'CustomHooks/useDocument';
import { RestaurantUserDocument } from 'types/shared/documents/restaurants';
import { db, FieldValue } from 'utils/firebase';
import logFirestoreError from 'utils/logFirestoreError';
import {
  EMPTY_ANSWER,
  MAX_OTHER_LENGTH,
  OTHER_OPTION,
  POS_OPTIONS,
  STAFF_PLANNING_OPTIONS,
  SurveyAnswer,
  SurveyOption,
  SurveyQuestion,
  TOOLS_SURVEY_ID,
  ToolsSurveyAnswers,
  ToolsSurveyResponse,
  buildToolsSurveyResponse,
  dismissForToday,
  isAnswerComplete,
  isDismissedToday,
  isSharedTabletAccount,
  shouldShowToolsSurvey,
  toolsSurveyAnswersPath,
} from './toolsSurvey';
import './styles.scss';

const STEPS: { field: SurveyQuestion; question: string; options: SurveyOption[] }[] = [
  {
    field: 'pos',
    question: 'Which POS system do you use in this restaurant?',
    options: POS_OPTIONS,
  },
  {
    field: 'staffPlanning',
    question: 'What do you use to plan your staff?',
    options: STAFF_PLANNING_OPTIONS,
  },
];

type ToolsSurveyModalProps = {
  otherDialogOpen?: boolean;
};

const ToolsSurveyModal = ({ otherDialogOpen = false }: ToolsSurveyModalProps) => {
  const { t } = useTranslation('dashboard');
  const { restaurantId, newToast } = useContext(RestaurantContext);
  const { uid, user, isGastronautAdmin, rolesLoaded } = useContext(AuthContext);

  const answersPath = restaurantId ? toolsSurveyAnswersPath(restaurantId) : '';
  const [response] = useDocument<ToolsSurveyResponse>(
    answersPath,
    TOOLS_SURVEY_ID,
    false,
    true,
  );

  const [membership] = useDocument<RestaurantUserDocument>(
    restaurantId ? `restaurants/${restaurantId}/users` : '',
    uid,
    false,
    true,
  );

  const [closed, setClosed] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<ToolsSurveyAnswers>({
    pos: EMPTY_ANSWER,
    staffPlanning: EMPTY_ANSWER,
  });
  const [saving, setSaving] = useState(false);

  const visible = shouldShowToolsSurvey({
    restaurantId,
    uid,
    rolesLoaded: rolesLoaded && !membership.loading,
    isSharedTablet: isSharedTabletAccount(membership.data),
    isGastronautAdmin,
    responseLoading: response.loading,
    responseExists: response.exists,
    responseError: response.error,
    dismissedToday: closed || (!!uid && isDismissedToday(window.localStorage, uid)),
    otherDialogOpen,
  });

  if (!visible || !restaurantId || !uid) return null;

  const current = STEPS[step];
  const answer = answers[current.field];
  const isLastStep = step === STEPS.length - 1;

  const setAnswer = (next: Partial<SurveyAnswer>) =>
    setAnswers((all) => ({
      ...all,
      [current.field]: { ...all[current.field], ...next },
    }));

  const answerDoc = db.collection(answersPath).doc(TOOLS_SURVEY_ID);

  const handleDismiss = () => {
    dismissForToday(window.localStorage, uid);
    setClosed(true);
  };

  const handleSubmit = async () => {
    const userName =
      user?.data?.displayName || user?.data?.email || '';

    setSaving(true);
    try {
      await answerDoc.set({
        ...buildToolsSurveyResponse({ restaurantId, uid, userName, answers }),
        createdAt: FieldValue.serverTimestamp(),
      });
      setClosed(true);
      newToast('Thank you for your answer!', Severity.SUCCESS, 'dashboard');
    } catch (error) {
      // The rules allow one answer per restaurant; a colleague may have been faster.
      const answeredMeanwhile = await answerDoc
        .get()
        .then((doc) => doc.exists)
        .catch(() => false);

      if (answeredMeanwhile) {
        setClosed(true);
        newToast(
          'Someone has already answered for this restaurant.',
          Severity.INFO,
          'dashboard',
        );
      } else {
        logFirestoreError('ToolsSurveyModal', answerDoc.path, error);
        newToast(
          'Could not save your answer. Please try again.',
          Severity.ERROR,
          'dashboard',
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={handleDismiss}
      stopAwayClick
      maxWidth="sm"
      fullWidth
      className="tools-survey"
      title="Quick question"
      titleTranslation="dashboard"
      bottomPart={
        <Box flex className="tools-survey-footer">
          <Typography variant="text-4" color="subdued" translation={null}>
            {t('Step {{step}} of {{total}}', {
              step: step + 1,
              total: STEPS.length,
            })}
          </Typography>
          <Box flex width="auto" className="tools-survey-actions">
            {step > 0 && (
              <Button
                translation="dashboard"
                disabled={saving}
                onClick={() => setStep((s) => s - 1)}
              >
                Back
              </Button>
            )}
            <Button
              variant="primary"
              translation="dashboard"
              disabled={!isAnswerComplete(answer)}
              loading={saving}
              onClick={() =>
                isLastStep ? handleSubmit() : setStep((s) => s + 1)
              }
            >
              {isLastStep ? 'Submit' : 'Next'}
            </Button>
          </Box>
        </Box>
      }
    >
      <Box className="tools-survey-body">
        <Typography variant="text-3" color="subdued" translation={null} block>
          {t('Your answer helps us decide which integrations we build next.')}
        </Typography>
        <Typography
          variant="h5"
          translation={null}
          block
          id="tools-survey-question"
          className="tools-survey-question"
        >
          {t(current.question)}
        </Typography>
        <SurveyChoices
          options={current.options}
          value={answer.choice}
          onChange={(choice) => setAnswer({ choice })}
        />
        {answer.choice === OTHER_OPTION && (
          <TextField
            autoFocus
            fullWidth
            label="Which one?"
            labelTranslation="dashboard"
            value={answer.other}
            onChange={(e) => setAnswer({ other: e.target.value })}
            inputProps={{ maxLength: MAX_OTHER_LENGTH }}
          />
        )}
      </Box>
    </Modal>
  );
};

type SurveyChoicesProps = {
  options: SurveyOption[];
  value: string;
  onChange: (value: string) => void;
};

// Brands as a logo grid; "other" and "none" as wide tiles below it.
// Native radio inputs keep keyboard use and screen readers working.
const SurveyChoices = ({ options, value, onChange }: SurveyChoicesProps) => {
  const { t } = useTranslation('dashboard');
  const brands = options.filter((option) => !!option.logo);
  const fallbacks = options.filter((option) => !option.logo);

  const tile = (option: SurveyOption) => (
    <label
      key={option.value}
      className={[
        'tools-survey-tile',
        option.logo ? 'brand' : 'fallback',
        value === option.value ? 'selected' : '',
      ].join(' ')}
    >
      <input
        type="radio"
        name="tools-survey-choice"
        className="tools-survey-input"
        value={option.value}
        checked={value === option.value}
        onChange={() => onChange(option.value)}
      />
      {option.logo && (
        <img src={option.logo} alt="" className="tools-survey-logo" />
      )}
      <span className="tools-survey-name">
        {option.translateLabel ? t(option.label) : option.label}
      </span>
    </label>
  );

  return (
    <div
      role="radiogroup"
      aria-labelledby="tools-survey-question"
      className="tools-survey-choices"
    >
      <div className="tools-survey-grid">{brands.map(tile)}</div>
      <div className="tools-survey-fallbacks">{fallbacks.map(tile)}</div>
    </div>
  );
};

export default ToolsSurveyModal;
